import React, { useState, useEffect, useRef } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  useToast,
  ModalBody,
  ModalCloseButton,
  Select,
  FormControl,
  FormLabel,
  Image,
  VStack,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdDone, MdCancel, MdAdd, MdRemove } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const ServiceCategories = ({ serviceId }) => {
  const { t } = useTranslation();  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [editableCategory, setEditableCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInsertForm, setShowInsertForm] = useState(false);  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const imageInputRef = useRef(null);
  const editImageInputRef = useRef(null);
  
  const [newCategory, setNewCategory] = useState({
    name: "",
    translation_key: "",
    image: "",
    service_id: serviceId || "",
    translations: { 
      en: "", 
      fr: "",
      it: ""
    }
  });

  const toast = useToast();
  const [permissions, setPermissions] = useState({ 
    canList: true, 
    canEdit: true, 
    canDelete: true, 
    canInsert: true 
  });
  const pageId = 13; // Service Categories page ID

  // Load permissions
  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast]);

  // Fetch services for dropdown
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}/services?page_id=${pageId}`;
        const data = await fetchWithToken(url);
        setServices(data.data || []);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  // Fetch all categories or categories by service ID
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        let url = `${process.env.REACT_APP_API_URL}/service-categories?page_id=${pageId}`;
        
        if (serviceId) {
          url += `/show?service_id=${serviceId}`;
        }

        const data = await fetchWithToken(url);
        const categoriesData = data.data || [];
          if (serviceId) {
          setCategories(categoriesData);
          setFilteredCategories(categoriesData);
        } else {
          setCategories(categoriesData);
          setFilteredCategories(categoriesData);
        }
      } catch (error) {
        toast({
          title: "Failed to fetch service categories",
          description: error.message || "Something went wrong while fetching categories.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
    };

    if (permissions.canList) {
      fetchCategories();
    }
  }, [serviceId, permissions.canList, toast]);

  // Handle search
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = categories.filter((category) =>
      category.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCategories(filtered);
  };

  // Handle create category
  const handleCreateCategory = async () => {
    try {
      setLoading(true);
      
      if (!newCategory.name || !newCategory.translation_key || !newCategory.service_id) {
        toast({
          title: "Missing required fields",
          description: "Please fill in all required fields (name, translation key, and service).",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const data = {
        name: newCategory.name,
        translation_key: newCategory.translation_key,
        image: newCategory.image,
        service_id: parseInt(newCategory.service_id),
        translations: newCategory.translations
      };

      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-categories?page_id=${pageId}`,
        "POST",
        data
      );

      toast({
        title: "Service Category created successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // Reset form
      setNewCategory({
        name: "",
        translation_key: "",
        image: "",
        service_id: serviceId || "",
        translations: { en: "", ar: "", fr: "", it: "" }
      });
      
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }

      setShowInsertForm(false);
      
      // Refresh categories
      const url = serviceId 
        ? `${process.env.REACT_APP_API_URL}/service-categories/show?service_id=${serviceId}&page_id=${pageId}`
        : `${process.env.REACT_APP_API_URL}/service-categories?page_id=${pageId}`;
      
      const response = await fetchWithToken(url);
      const categoriesData = response.data || [];
      setCategories(categoriesData);
      setFilteredCategories(categoriesData);

    } catch (error) {
      toast({
        title: "Failed to create category",
        description: error.message || "Something went wrong while creating the category.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit category
  const handleEditCategory = (category) => {
    setEditableCategory({ ...category });
  };
  // Handle save category
const handleSaveCategory = async () => {
  try {
    setLoading(true);

    // Construct the data object
    const data = {
      id: editableCategory.id,
      name: editableCategory.name,
      translation_key: editableCategory.translation_key,
      service_id: parseInt(editableCategory.service_id),
      translations: editableCategory.translations,
    };

    // Only include the image if it has been updated
    if (editableCategory.image && editImageInputRef.current?.files[0]) {
      data.image = editableCategory.image;
    }

    console.log("Updating category with data:", data);

    await fetchWithToken(
      `${process.env.REACT_APP_API_URL}/service-categories/update?page_id=${pageId}`,
      "PATCH",
      data
    );

    toast({
      title: "Category updated successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });

    // Update local state
    setCategories((prev) =>
      prev.map((cat) => (cat.id === editableCategory.id ? editableCategory : cat))
    );
    setFilteredCategories((prev) =>
      prev.map((cat) => (cat.id === editableCategory.id ? editableCategory : cat))
    );

    if (editImageInputRef.current) {
      editImageInputRef.current.value = "";
    }

    setEditableCategory(null);
  } catch (error) {
    toast({
      title: "Failed to update category",
      description: error.message || "Something went wrong while updating the category.",
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  } finally {
    setLoading(false);
  }
};
  // Handle delete category
  const handleDeleteCategory = async (categoryId) => {
    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-categories/delete?page_id=${pageId}`,
        "DELETE",
        { id: categoryId }
      );

      toast({
        title: "Category deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
      setFilteredCategories((prev) => prev.filter((cat) => cat.id !== categoryId));

    } catch (error) {
      toast({
        title: "Failed to delete category",
        description: error.message || "Something went wrong while deleting the category.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Handle image upload for new category
  const handleImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) {
          setEditableCategory({
            ...editableCategory,
            image: reader.result,
          });
        } else {
          setNewCategory({
            ...newCategory,
            image: reader.result,
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
     <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ base: "scroll", lg: "hidden" }}
        boxShadow="lg"
      >
      <Flex
        px={{ base: "10px", md: "25px" }}
        justify="space-between"
        mb="20px"
        align="center"
        flexWrap="wrap"
      >
        <Flex align="center">
          <Text
            color="black"
            fontSize={{ base: "18px", md: "22px" }}
            fontWeight="700"
            lineHeight="100%"
          >
            {serviceId ? t("Service Categories") : t("All Service Categories")}
          </Text>
          <Button
            onClick={() => setShowInsertForm(!showInsertForm)}
            isDisabled={!permissions.canInsert}
            ml={4}
            leftIcon={showInsertForm ? <MdRemove /> : <MdAdd />}
            colorScheme={showInsertForm ? "red" : "cyan"}
            color="white"
            p={4}
            borderRadius="full"
            boxShadow="md"
            _hover={{
              bg: showInsertForm ? "red.600" : "cyan.600",
              transform: "scale(1.05)",
            }}
            _active={{
              bg: showInsertForm ? "red.700" : "cyan.700",
              transform: "scale(0.95)",
            }}
            transition="all 0.2s"
          >
            {showInsertForm ? t("Cancel") : t("Add Category")}          </Button>
        </Flex>
        
        <Input
          placeholder={t("Search categories")}
          value={searchQuery}
          onChange={handleSearch}
          size="sm"
          w={{ base: "100%", md: "200px" }}
          mt={{ base: 2, md: 0 }}
        />
      </Flex>

      {/* Insert Category Modal */}
      <Modal
        isOpen={showInsertForm}
        onClose={() => setShowInsertForm(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Create Service Category")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color="blue.600">{t("Category Name")}</FormLabel>
                <Input
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name: e.target.value })
                  }
                  placeholder={t("Enter category name")}
                />
              </FormControl>

              <FormControl isRequired  >
                <FormLabel color="blue.600">{t("Translation Key")}</FormLabel>
                <Input
                  value={newCategory.translation_key}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, translation_key: e.target.value })
                  }
                  placeholder={t("Enter unique translation key")}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="blue.600">{t("Service")}</FormLabel>
                <Select
                  value={newCategory.service_id}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, service_id: e.target.value })
                  }
                  placeholder={t("Select a service")}
                >
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color="blue.600">{t("Category Image")}</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  onChange={(e) => handleImageUpload(e, false)}
                />
                 {/* Always show the helper text */}
    <Text fontSize="sm" color="gray.500" mt={2}>
      {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
    </Text>
                {newCategory.image && (
                  <Image
                    src={newCategory.image}
                    alt="Preview"
                    boxSize="100px"
                    objectFit="cover"
                    borderRadius="md"
                    mt={2}
                  />
                )}
              </FormControl>

              <FormControl>
                <FormLabel color="blue.600">{t("Translations")}</FormLabel>
                <VStack spacing={2}>
                  <HStack w="100%">
                    <Text minW="40px" color="blue.600">EN:</Text>
                    <Input
                      value={newCategory.translations.en}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          translations: { ...newCategory.translations, en: e.target.value }
                        })
                      }
                      placeholder={t("English translation")}
                    />
                  </HStack>
                  {/* <HStack w="100%">
                    <Text minW="40px" color="blue.600">AR:</Text>
                    <Input
                      value={newCategory.translations.ar}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          translations: { ...newCategory.translations, ar: e.target.value }
                        })
                      }
                      placeholder={t("Arabic translation")}
                    />
                  </HStack> */}
                  <HStack w="100%">
                    <Text minW="40px" color="blue.600">FR:</Text>
                    <Input
                      value={newCategory.translations.fr}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          translations: { ...newCategory.translations, fr: e.target.value }
                        })
                      }
                      placeholder={t("French translation")}
                    />
                  </HStack>
                  <HStack w="100%">
                    <Text minW="40px" color="blue.600">IT:</Text>
                    <Input
                      value={newCategory.translations.it}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          translations: { ...newCategory.translations, it: e.target.value }
                        })
                      }
                      placeholder={t("Italian translation")}
                    />
                  </HStack>
                </VStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="cyan"
              mr={3}
              onClick={handleCreateCategory}
              isLoading={loading}
              color={"white"}
            >
              {t("Create")}
            </Button>
            <Button variant="ghost" onClick={() => setShowInsertForm(false)}>
              {t("Cancel")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Categories Table */}
      <Box overflowY="auto" maxHeight="400px">
        <Table variant="striped" colorScheme="gray" mb="24px" size="sm">
   <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
               <Tr bg='rgb(69, 50, 240)' textColor="white" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
      <Th color="white">{t("Index")}</Th>
      <Th color="white">{t("Name")}</Th>
      <Th color="white">{t("Translations")}</Th>
      <Th color="white">{t("Service")}</Th>
      <Th color="white">{t("Image")}</Th>
      <Th color="white">{t("Actions")}</Th>
    </Tr>
  </Thead>

  <Tbody>
    {filteredCategories.map((category, index) => (
      <Tr
        key={category.id}
        bg={editableCategory?.id === category.id ? "blue.50" : "transparent"}
        borderWidth={editableCategory?.id === category.id ? "2px" : "0"}
        borderColor={editableCategory?.id === category.id ? "blue.400" : "transparent"}
        _hover={{
          bg: editableCategory?.id === category.id ? "blue.100" : "gray.50",
        }}
      >
        {/* Index */}
        <Td>{index + 1}</Td>

        {/* Name */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Input
              value={editableCategory.name || ""}
              onChange={(e) =>
                setEditableCategory({ ...editableCategory, name: e.target.value })
              }
              fontSize={{ base: "sm", md: "md" }}
              focusBorderColor="blue.400"
              borderColor="blue.300"
            />
          ) : (
            <Text fontSize={{ base: "sm", md: "md" }}>{category.name}</Text>
          )}
        </Td>

        {/* Translations */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Flex direction="column" gap={2}>
              <Flex align="center">
                <Text fontWeight="bold" minWidth="60px" mr={2}>Key:</Text>
                <Input
                  value={editableCategory.translation_key || ""}
                  size="sm"
                  isDisabled
                  focusBorderColor="blue.400"
                  borderColor="blue.300"
                />
              </Flex>
              <Flex align="center">
                <Text fontWeight="bold" minWidth="60px" mr={2}>FR:</Text>
                <Input
                  value={editableCategory.translations?.fr || ""}
                  onChange={(e) =>
                    setEditableCategory({
                      ...editableCategory,
                      translations: { ...editableCategory.translations, fr: e.target.value },
                    })
                  }
                  size="sm"
                  focusBorderColor="blue.400"
                  borderColor="blue.300"
                />
              </Flex>
              <Flex align="center">
                <Text fontWeight="bold" minWidth="60px" mr={2}>IT:</Text>
                <Input
                  value={editableCategory.translations?.it || ""}
                  onChange={(e) =>
                    setEditableCategory({
                      ...editableCategory,
                      translations: { ...editableCategory.translations, it: e.target.value },
                    })
                  }
                  size="sm"
                  focusBorderColor="blue.400"
                  borderColor="blue.300"
                />
              </Flex>
            </Flex>
          ) : (
            <Flex direction="column">
              {category.translation_key && (
                <Text fontSize="sm" fontWeight="medium">Key: {category.translation_key}</Text>
              )}
              {category.translations?.fr && (
                <Text fontSize="sm">FR: {category.translations.fr}</Text>
              )}
              {category.translations?.it && (
                <Text fontSize="sm">IT: {category.translations.it}</Text>
              )}
              {!category.translation_key &&
                !category.translations?.fr &&
                !category.translations?.it && (
                  <Text fontSize="sm" color="gray.500">{t("No translations")}</Text>
                )}
            </Flex>
          )}
        </Td>

        {/* Service */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Select
              value={editableCategory.service_id || ""}
              onChange={(e) =>
                setEditableCategory({
                  ...editableCategory,
                  service_id: e.target.value,
                })
              }
            >
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Select>
          ) : (
            services.find(s => s.id === category.service_id)?.name || ""
          )}
        </Td>

        {/* Image */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Flex direction="column" align="center">
              {editableCategory.image && (
                <Image
                  src={editableCategory.image}
                  alt={editableCategory.name}
                  boxSize="50px"
                  objectFit="cover"
                  borderRadius="md"
                  mb={2}
                />
              )}
              <Input
                type="file"
                accept="image/*"
                ref={editImageInputRef}
                onChange={(e) => handleImageUpload(e, true)}
                size="sm"
              />
            </Flex>
          ) : category.image ? (
            <Image
              src={category.image}
              alt={category.name}
              boxSize="50px"
              objectFit="cover"
              borderRadius="md"
            />
          ) : (
            <Text>{t("No Image")}</Text>
          )}
        </Td>

        {/* Actions */}
        <Td>
          {editableCategory?.id === category.id ? (
            <Flex gap={2}>
              <Button
                colorScheme="green"
                size="sm"
                onClick={handleSaveCategory}
                leftIcon={<MdDone />}
                isLoading={loading}
                borderRadius="full"
              >
                {t("Save")}
              </Button>
              <Button
                colorScheme="red"
                ml={2}
                size="sm"
                onClick={() => setEditableCategory(null)}
                leftIcon={<MdCancel />}
                borderRadius="full"
              >
                {t("Cancel")}
              </Button>
            </Flex>
          ) : (
            <Flex gap={2}>
              <Button
                colorScheme="cyan"
                isDisabled={!permissions.canEdit}
                size="sm"
                onClick={() => handleEditCategory(category)}
                leftIcon={<MdEdit />}
                borderRadius="full"
                color="white"
              >
                {t("Edit")}
              </Button>
              {/* <Button
                colorScheme="red"
                size="sm"
                isDisabled={!permissions.canDelete}
                ml={2}
                onClick={() => {
                  setCategoryToDelete(category);
                  setDeleteModalOpen(true);
                }}
                leftIcon={<MdDelete />}
                borderRadius="full"
              >
                {t("Delete")}
              </Button> */}
            </Flex>
          )}
        </Td>
      </Tr>
    ))}
  </Tbody>
</Table>

        
        {filteredCategories.length === 0 && !loading && (
          <Flex justify="center" py={8}>
            <Text color="gray.500" fontSize="lg">
              {t("No service categories found")}
            </Text>
          </Flex>        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Confirm Delete")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {t("Are you sure you want to delete this category?")} 
              {categoryToDelete && (
                <Text as="span" fontWeight="bold" ml={1}>
                  "{categoryToDelete.name}"
                </Text>
              )}
            </Text>
            <Text mt={2} fontSize="sm" color="gray.500">
              {t("This action cannot be undone.")}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button 
              mr={3} 
              onClick={() => setDeleteModalOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button 
              colorScheme="red" 
              isLoading={loading}
              onClick={() => {
                if (categoryToDelete) {
                  handleDeleteCategory(categoryToDelete.id);
                  setDeleteModalOpen(false);
                  setCategoryToDelete(null);
                }
              }}
            >
              {t("Delete")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ServiceCategories;
